import { Injectable } from '@angular/core';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { CartActions } from '@spartacus/cart/base/core';
import { AuthStatePersistenceService, createFrom, EventService, RouterState, RoutingService } from '@spartacus/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, withLatestFrom } from 'rxjs/operators';
import { Ga4GgCartRemoveEntrySuccessEvent } from './ga4-gg-cart-remove-entry-success-event';
import { ActiveCartFacade } from '@spartacus/cart/base/root';

@Injectable({
  providedIn: 'root',
})
export class Ga4GgCartRemoveEntrySuccessEventBuilder {
  constructor(
    private eventService: EventService,
    private authStatePersistenceService: AuthStatePersistenceService,
    protected activeCartService: ActiveCartFacade,
    protected actionsSubject: ActionsSubject,
    protected event: EventService,
    protected routing: RoutingService
  ) {
    const ga4GgCartRemoveEntrySuccessClenUpEventSource = this.getAction(CartActions.CART_REMOVE_ENTRY_SUCCESS).pipe(
      map(() =>
        createFrom(Ga4GgCartRemoveEntrySuccessEvent, {
          ecommerce: null,
        })
      )
    );

    eventService.register(Ga4GgCartRemoveEntrySuccessEvent, ga4GgCartRemoveEntrySuccessClenUpEventSource);

    const ga4GgCartRemoveEntrySuccessEventSource = this.getAction(CartActions.CART_REMOVE_ENTRY_SUCCESS).pipe(
      switchMap((action) => {
        return of(action).pipe(withLatestFrom(this.activeCartService.getEntries(), this.routing.getRouterState()));
      }),
      map(([action, entries, routerState]) =>
        createFrom(Ga4GgCartRemoveEntrySuccessEvent, {
          event: 'remove_from_cart',
          visitor_type: authStatePersistenceService.isUserLoggedIn() ? 'logged_in' : 'logged-out',
          ecommerce: {
            currency: entries[Number(action.payload.entryNumber)].basePrice?.currencyIso,
            value:
              entries[Number(action.payload.entryNumber)].basePrice?.value! *
              entries[Number(action.payload.entryNumber)].quantity!,
            items: this.getItemFromSuccessfullyRemovedEntry(entries[Number(action.payload.entryNumber)], routerState),
          },
        })
      )
    );

    eventService.register(Ga4GgCartRemoveEntrySuccessEvent, ga4GgCartRemoveEntrySuccessEventSource);
  }

  private getItemFromSuccessfullyRemovedEntry(successfullyRemovedEntry, routerState: RouterState) {
    const items: {
      item_id?: string;
      item_name?: string;
      price?: number;
      quantity?: number;
      item_list_name?: string;
    }[] = [];

    const item = {
      item_id: successfullyRemovedEntry.product.code,
      item_name: successfullyRemovedEntry.product.modelMark,
      price: successfullyRemovedEntry.basePrice.value,
      quantity: successfullyRemovedEntry.quantity,
      item_list_name: routerState.state.semanticRoute === 'cart' ? 'Cart' : 'Minicart',
    };

    successfullyRemovedEntry.product.categoryHierarchy?.forEach((category, index) => {
      item['item_category' + (index.toString() !== '0' ? (index + 1).toString() : '')] = category.name;
    });

    Object.keys(item).forEach((key) => (item[key] === undefined ? delete item[key] : {}));

    items.push(item);

    return items;
  }

  protected getAction(actionType: string | string[]): Observable<{ type: string; payload?: any }> {
    return this.actionsSubject.pipe(ofType(...([] as string[]).concat(actionType)));
  }
}
